/* Exportadores 100% del lado del cliente para el Centro de Diseño VM.
   Documentos (PDF/CSV) y planos (imágenes/PDF) sin depender del backend. */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const PETROL = "#0c2b38";
const NEON = "#16a34a";

const n2 = (n) => Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (n) => `S/ ${n2(n)}`;
const stamp = () => new Date().toLocaleString("es-PE");
const fileStamp = () => new Date().toISOString().slice(0, 10);

export const VM_FACADES = {
  A: { img: "/vm/modelo-a.png", nombre: "Modelo A · Inmobiliario Clásico", desc: "Torre de cristal lateral, balcones con baranda de tubo cuadrado", label: "EDIFICIO 01" },
  B: { img: "/vm/modelo-b.png", nombre: "Modelo B · Minimalista Pro", desc: "Barandas de vidrio, panelado vertical gris oscuro a la izquierda", label: "EDIFICIO 02" },
  C: { img: "/vm/modelo-c.png", nombre: "Modelo C · Vanguardia", desc: "Marcos estructurales blancos y revestimiento tipo madera", label: "EDIFICIO 03" },
};
export const VM_DISTRIBS = {
  "01": { img: "/vm/dist-01.jpg", nombre: "Distribución 01", tag: "FUNCIONAL · Lineal", desc: "Práctica y económica" },
  "02": { img: "/vm/dist-02.jpg", nombre: "Distribución 02", tag: "CONTEMPORÁNEA · Integrada", desc: "Espacios más abiertos" },
  "03": { img: "/vm/dist-03.jpg", nombre: "Distribución 03", tag: "PREMIUM · Moderna", desc: "Mayor riqueza espacial" },
};
export const VM_RENDER_3D = "/vm/render-3d.jpg";

async function toDataUrl(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

function imgSize(dataUrl) {
  return new Promise((resolve) => {
    const im = new Image();
    im.onload = () => resolve({ w: im.width, h: im.height });
    im.onerror = () => resolve({ w: 4, h: 3 });
    im.src = dataUrl;
  });
}

function download(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function header(doc, title) {
  doc.setFillColor(PETROL);
  doc.rect(0, 0, 210, 22, "F");
  doc.setTextColor("#ffffff");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("CONSTRULINK · COREX", 14, 10);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor("#a7f3d0");
  doc.text("Vivienda Multifamiliar · Centro de Diseño", 14, 16);
  doc.setTextColor("#0f172a");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(title, 14, 33);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor("#64748b");
  doc.text(`Emitido: ${stamp()}`, 14, 39);
  doc.setTextColor("#0f172a");
}

function pisosLabel(p) {
  const n = Number(p?.niveles?.pisos) || 1;
  const seq = Array.from({ length: n }, (_, i) => i + 1).join(" + ");
  return `Pisos ${seq}${p?.niveles?.incluirParapeto ? " + Parapeto" : ""}`;
}

/* ── Documentos ─────────────────────────────────────────────── */
export function exportProgramaPDF(p) {
  const doc = new jsPDF();
  header(doc, "Programa Arquitectónico");
  const rows = (p.ambientes || []).map((a) => {
    const total = (Number(a.areaProp) || 0) * Math.max(1, Number(a.cantidad) || 1);
    return [a.nombre, String(a.piso), a.tipoPiso || "-", n2(a.areaMin), n2(a.areaProp), String(a.cantidad), n2(total)];
  });
  const totalTechada = (p.ambientes || [])
    .filter((a) => a.tipoPiso && a.tipoPiso !== "-")
    .reduce((s, a) => s + (Number(a.areaProp) || 0) * Math.max(1, Number(a.cantidad) || 1), 0);
  autoTable(doc, {
    startY: 44,
    head: [["Ambiente", "Piso", "Tipo de piso", "Á. mín (NTP)", "Á. prop (m²)", "Cant", "Á. total (m²)"]],
    body: rows,
    foot: [["", "", "", "", "", "TOTAL ÁREA TECHADA", `${n2(totalTechada)} m²`]],
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: PETROL, textColor: "#ffffff" },
    footStyles: { fillColor: "#e2e8f0", textColor: "#0f172a", fontStyle: "bold" },
  });
  doc.save(`VM_Programa_Arquitectonico_${fileStamp()}.pdf`);
}

export function exportResumenPDF(p, resumen) {
  const doc = new jsPDF();
  header(doc, "Memoria de Sustento de Presupuesto");
  const ancho = Number(p?.terreno?.ancho) || 0;
  const largo = Number(p?.terreno?.largo) || 0;
  const areaT = ancho * largo;
  const perim = (ancho + largo) * 2;
  const modo = resumen?.modo || p?.niveles?.modo || "ACUMULADA";
  const pisos = resumen?.pisos || Number(p?.niveles?.pisos) || 1;
  const modoLbl = modo === "ESPECIFICO" ? `Nivel específico ${pisos}` : (pisos > 1 ? `Acumulado · ${pisos} pisos` : "1 piso");
  // 1) Dimensiones del terreno y configuración
  autoTable(doc, {
    startY: 44,
    head: [["Parámetro de diseño", "Valor"]],
    body: [
      ["Proyecto", String(p?.nombre || "Vivienda Multifamiliar")],
      ["Ancho (frente)", `${n2(ancho)} m`],
      ["Largo (fondo)", `${n2(largo)} m`],
      ["Área del terreno", `${n2(areaT)} m²`],
      ["Perímetro", `${n2(perim)} m`],
      ["Altura 1° piso", `${n2(p?.terreno?.altura1 ?? 3)} m`],
      ["Altura típica (2°-5°)", `${n2(p?.terreno?.alturaTipica ?? 2.7)} m`],
      ["Altura parapeto", `${n2(p?.terreno?.alturaParapeto ?? 0.9)} m`],
      ["Modo de edificación", modoLbl],
      ["Configuración", pisosLabel(p)],
    ],
    styles: { fontSize: 9, cellPadding: 2.4 },
    headStyles: { fillColor: PETROL, textColor: "#ffffff" },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 70 } },
  });
  // 2) Resumen por niveles (multi-nivel)
  const niveles = (resumen && resumen.niveles) || [];
  if (niveles.length > 1) {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["Nivel", "Altura (m)", "Cimentación", "Factor altura", "Sobrecosto (S/)", "CD del nivel (S/)"]],
      body: niveles.map((nv) => [
        `Nivel ${nv.nivel}`, n2(nv.altura), nv.incluyeCimentacion ? "Sí (una vez)" : "No",
        `x${Number(nv.factorAltura || 1).toFixed(2)}`, n2(nv.sobrecostoAltura || 0), n2(nv.subtotalConAjuste ?? nv.subtotal),
      ]),
      foot: [["", "", "", "Ajuste global", n2(resumen.ajusteGlobal || 0), n2(resumen.costoDirectoConAjuste ?? resumen.costoDirecto)]],
      styles: { fontSize: 8.5, cellPadding: 2 },
      headStyles: { fillColor: "#1e293b", textColor: "#ffffff" },
      footStyles: { fillColor: NEON, textColor: "#ffffff", fontStyle: "bold" },
      columnStyles: { 1: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" } },
    });
    drawCurvaNiveles(doc, niveles, doc.lastAutoTable.finalY + 8);
  }
  // 3) Programa arquitectónico (ambientes)
  const isLibre = (a) => /libre|patio/i.test(a?.nombre || "");
  const ambRows = (p?.ambientes || []).map((a) => {
    const total = isLibre(a) ? (areaT * (Number(a.areaProp) || 0)) / 100 : (Number(a.areaProp) || 0) * (Number(a.cantidad) || 0);
    return [a.nombre, a.tipoPiso || "-", isLibre(a) ? `${n2(a.areaProp)} %` : n2(a.areaProp), String(a.cantidad), n2(total)];
  });
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 8,
    head: [["Ambiente", "Tipo de piso", "Á. propuesta", "Cantidad", "Á. total (m²)"]],
    body: ambRows,
    styles: { fontSize: 8, cellPadding: 1.8 },
    headStyles: { fillColor: PETROL, textColor: "#ffffff" },
  });
  // 4) Acabados de Piso por Ambiente
  const acabRows = (p?.ambientes || [])
    .filter((a) => a.tipoPiso && a.tipoPiso !== "-")
    .map((a) => {
      const area = isLibre(a) ? (areaT * (Number(a.areaProp) || 0)) / 100 : (Number(a.areaProp) || 0) * (Number(a.cantidad) || 0);
      return [a.nombre, a.tipoPiso, n2(a.areaProp), String(a.cantidad ?? 0), n2(area)];
    });
  {
    const yTitle = doc.lastAutoTable.finalY + 12;
    doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.setTextColor(PETROL);
    doc.text("Acabados de Piso por Ambiente", 14, yTitle - 4);
    autoTable(doc, {
      startY: yTitle,
      head: [["Ambiente", "Tipo de piso", "Á. prop. (m²)", "Cant.", "Á. piso (m²)"]],
      body: acabRows,
      styles: { fontSize: 8, cellPadding: 1.8 },
      headStyles: { fillColor: "#334155", textColor: "#ffffff" },
    });
  }
  // 5) Balance de Acabados por Material (motor)
  const acab = (resumen && resumen.acabados) || [];
  if (acab.length) {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["Material", "Metrado total (m²)", "Costo directo (S/)"]],
      body: acab.map((a) => [a.tipo, n2(a.metrado_m2), n2(a.costo_total)]),
      styles: { fontSize: 9, cellPadding: 2.2 },
      headStyles: { fillColor: "#0f766e", textColor: "#ffffff" },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
    });
  }
  // 6) Resumen de presupuesto por capítulos (motor determinista · espejo del Excel)
  const chapters = (resumen && resumen.chapters) || [];
  const ajuste = (resumen && resumen.ajusteGlobal) || 0;
  const cdAjuste = (resumen && (resumen.costoDirectoConAjuste ?? resumen.costoDirecto));
  if (chapters.length) {
    const body = chapters.map((c) => {
      let nombre = c.nombre.replace(/^\d+\s*/, "");
      if (c.num === 2 && ajuste > 0) nombre += " (incl. sobrecosto por altura)";
      return [String(c.num).padStart(2, "0"), nombre, n2(c.subtotal)];
    });
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["N°", "Especialidad (capítulo)", "Subtotal (S/)"]],
      body,
      foot: [["", "TOTAL COSTO DIRECTO", n2(cdAjuste)]],
      styles: { fontSize: 9, cellPadding: 2.4 },
      headStyles: { fillColor: PETROL, textColor: "#ffffff" },
      footStyles: { fillColor: NEON, textColor: "#ffffff", fontStyle: "bold" },
      columnStyles: { 2: { halign: "right" } },
    });
    // 7) Pie de presupuesto (sobre CD + Ajuste Global)
    const pie = resumen && resumen.pie;
    if (pie) {
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [["Pie de presupuesto (sobre CD + Ajuste Global)", "Monto (S/)"]],
        body: [
          ["Costo Directo + Ajuste Global", n2(pie.costoDirecto)],
          ["Gastos Generales", n2(pie.generalExpenses)],
          ["Utilidad (10%)", n2(pie.utility)],
          ["Subtotal", n2(pie.subtotal)],
          ["IGV (18%)", n2(pie.igv)],
          ["Valor Referencial", n2(pie.valorReferencial)],
          ["Supervisión (2.5%)", n2(pie.supervision)],
        ],
        foot: [["PRESUPUESTO TOTAL", n2(pie.total)]],
        styles: { fontSize: 9, cellPadding: 2.3 },
        headStyles: { fillColor: "#0f172a", textColor: "#ffffff" },
        footStyles: { fillColor: PETROL, textColor: "#ffffff", fontStyle: "bold" },
        columnStyles: { 1: { halign: "right" } },
      });
    }
    // Nota técnica del Factor de Complejidad por Altura
    let y = doc.lastAutoTable.finalY + 7;
    if (y > 250) { doc.addPage(); y = 30; }
    doc.setFontSize(8.5); doc.setFont("helvetica", "bold"); doc.setTextColor(PETROL);
    doc.text("Nota técnica — Factor de Complejidad por Altura", 14, y);
    doc.setFont("helvetica", "normal"); doc.setFontSize(7.8); doc.setTextColor("#475569");
    const nota = [
      "La partida 02.05 «Sobrecosto por transporte vertical y rendimiento» recoge el mayor costo de mano de obra y",
      "transporte de materiales en niveles superiores, sin alterar los Análisis de Precios Unitarios (APU) maestros.",
      "Factores aplicados sobre el Costo Directo de cada nivel: Pisos 1 y 2 = 1.00 (sin sobrecosto); Piso 3 = +3%;",
      "Piso 4 = +6%; Piso 5 = +9%. El ajuste es aditivo al Costo Directo total y la base del pie de presupuesto",
      "(Gastos Generales, Utilidad, IGV y Supervisión) se calcula sobre [CD de Niveles + Ajuste Global].",
    ];
    nota.forEach((ln, i) => doc.text(ln, 14, y + 5 + i * 4));
  } else {
    doc.setFontSize(10); doc.setTextColor("#b45309");
    doc.text("Nota: no se pudo obtener el resumen por capítulos del motor. Guarda un cambio en Inicio y reintenta.", 14, doc.lastAutoTable.finalY + 10);
  }
  signatureBlock(doc, p);
  doc.save(`VM_Memoria_Presupuesto_${fileStamp()}.pdf`);
}

/* Gráfico de barras — Curva de Costos por Nivel (CD del nivel incl. sobrecosto por altura). */
function drawCurvaNiveles(doc, niveles, startY) {
  let y = startY;
  if (y > 235) { doc.addPage(); y = 30; }
  doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.setTextColor("#0f766e");
  doc.text("Curva de Costos por Nivel (S/)", 14, y);
  y += 5;
  const x0 = 34, maxW = 150;
  const maxV = Math.max(...niveles.map((n) => n.subtotalConAjuste ?? n.subtotal ?? 0), 1);
  const rowH = 8;
  niveles.forEach((nv) => {
    const val = nv.subtotalConAjuste ?? nv.subtotal ?? 0;
    const rate = (nv.factorAltura || 1) - 1;
    const w = Math.max((val / maxV) * maxW, 1);
    const col = rate <= 0 ? [22, 163, 74] : rate <= 0.03 ? [132, 204, 22] : rate <= 0.06 ? [234, 179, 8] : [217, 119, 6];
    doc.setFontSize(7.5); doc.setFont("helvetica", "bold"); doc.setTextColor("#334155");
    doc.text(`P${nv.nivel}`, 14, y + rowH - 2.5);
    doc.setFillColor(col[0], col[1], col[2]);
    doc.rect(x0, y, w, rowH - 2, "F");
    doc.setFontSize(7); doc.setFont("helvetica", "normal"); doc.setTextColor("#0f172a");
    doc.text(`${n2(val)}${rate > 0 ? `  (+${Math.round(rate * 100)}%)` : ""}`, x0 + w + 2, y + rowH - 3);
    y += rowH;
  });
  doc.setFontSize(6.5); doc.setTextColor("#94a3b8"); doc.setFont("helvetica", "normal");
  doc.text("Verde = sin sobrecosto · Ámbar = mayor complejidad por altura (transporte vertical y rendimiento).", 14, y + 2);
}

/* Bloque de firma profesional (Ingeniero Responsable + CIP) para reportes PDF. */
function signatureBlock(doc, p) {
  let y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 18 : 250;
  if (y > 265) { doc.addPage(); y = 40; }
  const ing = (p?.firma?.ingeniero || "").trim();
  const cip = (p?.firma?.cip || "").trim();
  doc.setDrawColor("#94a3b8");
  doc.line(30, y, 100, y);
  doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.setTextColor("#0f172a");
  doc.text(ing || "Ingeniero Responsable", 65, y + 5, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor("#64748b");
  doc.text(cip ? `CIP ${cip}` : "CIP —", 65, y + 10, { align: "center" });
  doc.text("Ingeniero Responsable", 65, y + 15, { align: "center" });
}

export function exportFichaPDF(p, geo, control) {
  const doc = new jsPDF();
  header(doc, "Ficha Integral del Proyecto");
  autoTable(doc, {
    startY: 44,
    theme: "plain",
    body: [
      ["Proyecto", p.nombre],
      ["Configuración", pisosLabel(p)],
      ["Terreno", `${n2(p.terreno.ancho)} m × ${n2(p.terreno.largo)} m · Área ${n2(geo.area)} m² · Perímetro ${n2(geo.perimetro)} m`],
      ["Altura total estimada", `${n2(geo.alturaTotal)} m${p.niveles.incluirParapeto ? " + parapeto" : ""}`],
      ["Modelo (fachada)", VM_FACADES[p.diseno.modeloId]?.nombre || p.diseno.modeloId],
      ["Distribución", VM_DISTRIBS[p.diseno.distribucionId]?.nombre || p.diseno.distribucionId],
    ],
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 55, textColor: PETROL } },
  });
  let y = doc.lastAutoTable.finalY + 4;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Programa Arquitectónico", 14, y);
  autoTable(doc, {
    startY: y + 3,
    head: [["Ambiente", "Piso", "Tipo", "Á. prop", "Cant", "Á. total"]],
    body: (p.ambientes || []).map((a) => [
      a.nombre, String(a.piso), a.tipoPiso || "-", n2(a.areaProp), String(a.cantidad),
      n2((Number(a.areaProp) || 0) * Math.max(1, Number(a.cantidad) || 1)),
    ]),
    styles: { fontSize: 7.5, cellPadding: 1.5 },
    headStyles: { fillColor: PETROL, textColor: "#ffffff" },
  });
  y = doc.lastAutoTable.finalY + 6;
  doc.setFillColor(control?.compat ? "#dcfce7" : "#fef3c7");
  doc.rect(14, y, 182, 10, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor("#0f172a");
  doc.text(`Control de área · Utilización ${(control?.util || 0).toFixed(1)}% ${control?.compat ? "· Configuración compatible" : "· Fuera de rango"}`, 18, y + 6.5);
  if (p.presupuesto) {
    y += 16;
    doc.setTextColor("#0f172a");
    doc.text(`Presupuesto referencial total: ${money(p.presupuesto.total)}`, 14, y);
  }
  doc.save(`VM_Ficha_Proyecto_${fileStamp()}.pdf`);
}

export function exportCSV(p) {
  const head = ["Ambiente", "Piso", "Tipo de piso", "Area minima (NTP)", "Area propuesta (m2)", "Cantidad", "Area total (m2)"];
  const lines = [head.join(",")];
  (p.ambientes || []).forEach((a) => {
    const total = (Number(a.areaProp) || 0) * Math.max(1, Number(a.cantidad) || 1);
    lines.push([`"${a.nombre}"`, a.piso, `"${a.tipoPiso || "-"}"`, a.areaMin, a.areaProp, a.cantidad, total.toFixed(2)].join(","));
  });
  download(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" }), `VM_Programa_${fileStamp()}.csv`);
}

/* ── Planos ─────────────────────────────────────────────────── */
export async function exportImagen(url, name) {
  const res = await fetch(url);
  download(await res.blob(), name);
}

export async function exportPlano2D(p) {
  const d = VM_DISTRIBS[p.diseno.distribucionId];
  await exportImagen(d.img, `VM_Plano2D_${p.diseno.distribucionId}_${fileStamp()}.jpg`);
}

export async function exportRender3D(p) {
  await exportImagen(VM_RENDER_3D, `VM_Render3D_${fileStamp()}.jpg`);
}

export async function exportPlanos2D3DPDF(p) {
  const doc = new jsPDF();
  header(doc, "Planos · 2D + 3D");
  doc.setFontSize(9);
  doc.setTextColor("#0f172a");
  doc.text(`Modelo: ${VM_FACADES[p.diseno.modeloId]?.nombre}`, 14, 47);
  doc.text(`Distribución: ${VM_DISTRIBS[p.diseno.distribucionId]?.nombre}`, 14, 52);

  const facade = await toDataUrl(VM_FACADES[p.diseno.modeloId].img);
  const plano = await toDataUrl(VM_DISTRIBS[p.diseno.distribucionId].img);
  const render = await toDataUrl(VM_RENDER_3D);

  const place = async (dataUrl, x, y, maxW, maxH, caption) => {
    const { w, h } = await imgSize(dataUrl);
    const r = Math.min(maxW / w, maxH / h);
    const dw = w * r, dh = h * r;
    doc.addImage(dataUrl, "JPEG", x, y, dw, dh);
    doc.setFontSize(8);
    doc.setTextColor("#64748b");
    doc.text(caption, x, y + dh + 4);
    return y + dh + 8;
  };

  let y = 58;
  y = await place(plano, 14, y, 88, 70, "Plano 2D · Distribución seleccionada");
  await place(facade, 110, 58, 86, 55, "Fachada seleccionada");
  await place(render, 110, 120, 86, 55, "Modelo 3D · Render");
  await place(render, 14, y, 88, 70, "Vista integral 3D");
  doc.save(`VM_Planos_2D_3D_${fileStamp()}.pdf`);
}
